import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { SMART_SCHEDULE_TIMEZONE, toSmartScheduleKst } from '../smartSchedule/date';
import {
    QuickMoveDraft,
    QuickMoveKind,
    QuickMoveShiftAvailability,
    QuickMoveTimeValidation,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 시트 시간 규칙(§6-1). 웹에서만 강제한다 — 관리자 PATCH 에는 시간 관계 검사가 없다.
 *
 * - 시작·종료는 같은 KST 날짜 안. 종료가 다음 날로 넘는 것은 **정확히 다음 날 00:00** 만.
 * - 기존 길이를 보존하다 자정을 넘기면 저장 불가 — 길이를 몰래 줄이지 않는다.
 * - 이미 다음 날 00:00 인 종료에 하루를 다시 더하지 않는다(endMinutes=1440 으로 표현).
 * - 종료 > 시작. 최소 길이: PT 는 원래 길이가 10분 미만이면 그 길이까지, 그 외 10분. 일반일정 10분.
 * - 날짜·시작만 바꾸면 **실제 기존 길이** 보존(수강권 기본 길이로 재계산하지 않음).
 *
 * 이유: PT preCheck 는 시작한 날 안의 일정만 조회해 다음 날 새벽 충돌을 놓친다.
 */
export const QUICK_MOVE_STEP_MINUTES = 10;
export const QUICK_MOVE_MIN_DURATION_MINUTES = 10;
export const QUICK_MOVE_DAY_MINUTES = 24 * 60;

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface QuickMoveTimeOriginal {
    kind: QuickMoveKind;
    startTime: string;
    endTime: string;
}

const dayStartOf = (date: string): Dayjs => dayjs.tz(`${date} 00:00`, SMART_SCHEDULE_TIMEZONE);

const isValidDateKey = (date: string): boolean => {
    if (typeof date !== 'string' || !DATE_KEY_PATTERN.test(date)) return false;
    const start = dayStartOf(date);
    return start.isValid() && start.format('YYYY-MM-DD') === date;
};

const minutesFromDayStart = (date: string, value: string): number =>
    toSmartScheduleKst(value).diff(dayStartOf(date), 'millisecond') / 60000;

const toIsoAt = (date: string, minutes: number): string =>
    dayStartOf(date).add(Math.round(minutes * 60000), 'millisecond').toISOString();

const sameInstant = (left: string, right: string): boolean =>
    toSmartScheduleKst(left).valueOf() === toSmartScheduleKst(right).valueOf();

/** 원래 일정의 실제 길이(분). 0 이하·파싱 실패면 0. */
export const getQuickMoveOriginalDurationMinutes = (original: QuickMoveTimeOriginal): number => {
    const start = toSmartScheduleKst(original.startTime);
    const end = toSmartScheduleKst(original.endTime);
    if (!start.isValid() || !end.isValid()) return 0;
    return Math.max(0, end.diff(start, 'millisecond') / 60000);
};

/** 허용 최소 길이(분). PT 는 원래 길이가 10분 미만이면 그 길이까지 허용. */
export const getQuickMoveMinDurationMinutes = (original: QuickMoveTimeOriginal): number => {
    if (original.kind !== 'private') return QUICK_MOVE_MIN_DURATION_MINUTES;
    const originalDuration = getQuickMoveOriginalDurationMinutes(original);
    return originalDuration > 0
        ? Math.min(QUICK_MOVE_MIN_DURATION_MINUTES, originalDuration)
        : QUICK_MOVE_MIN_DURATION_MINUTES;
};

/** 원본 그대로의 시트 값. 다음 날 00:00 종료는 endMinutes=1440 이 된다. */
export const createQuickMoveDraft = (original: QuickMoveTimeOriginal): QuickMoveDraft => {
    const date = toSmartScheduleKst(original.startTime).format('YYYY-MM-DD');
    return {
        date,
        startMinutes: minutesFromDayStart(date, original.startTime),
        endMinutes: minutesFromDayStart(date, original.endTime),
    };
};

const durationOf = (draft: QuickMoveDraft): number => draft.endMinutes - draft.startMinutes;

const snapToStep = (minutes: number): number =>
    Math.round(minutes / QUICK_MOVE_STEP_MINUTES) * QUICK_MOVE_STEP_MINUTES;

/** 10분 격자에서 한 칸 이동. 격자 밖 값(예: 10:05)은 그 방향의 가장 가까운 격자로 간다. */
const stepOnGrid = (minutes: number, direction: 1 | -1): number => {
    const step = QUICK_MOVE_STEP_MINUTES;
    const epsilon = 1e-9;
    return direction > 0
        ? Math.floor(minutes / step + epsilon) * step + step
        : Math.ceil(minutes / step - epsilon) * step - step;
};

/** 날짜만 바꾼다. 시작 시각과 기존 길이를 그대로 보존한다. */
export const setQuickMoveDate = (draft: QuickMoveDraft, date: string): QuickMoveDraft => ({
    ...draft,
    date,
});

/**
 * 시작 시각을 정한다(10분 스냅). 기존 길이를 보존해 종료가 따라 움직인다.
 * 드래그로 놓은 위치도 이 함수로 넣는다.
 */
export const setQuickMoveStartMinutes = (draft: QuickMoveDraft, startMinutes: number): QuickMoveDraft => {
    const duration = durationOf(draft);
    const snapped = snapToStep(startMinutes);
    return { ...draft, startMinutes: snapped, endMinutes: snapped + duration };
};

/** 날짜와 시작을 한 번에(드래그 결과). 길이 보존·10분 스냅. */
export const moveQuickMoveDraft = (
    draft: QuickMoveDraft,
    target: { date?: string; startMinutes: number }
): QuickMoveDraft =>
    setQuickMoveStartMinutes(target.date ? setQuickMoveDate(draft, target.date) : draft, target.startMinutes);

/** 시작 ±10분. 길이 보존(종료도 함께 움직인다). */
export const shiftQuickMoveStart = (draft: QuickMoveDraft, direction: 1 | -1): QuickMoveDraft => {
    const duration = durationOf(draft);
    const nextStart = stepOnGrid(draft.startMinutes, direction);
    return { ...draft, startMinutes: nextStart, endMinutes: nextStart + duration };
};

/** 종료 ±10분. 시작은 그대로(길이가 바뀐다). */
export const shiftQuickMoveEnd = (draft: QuickMoveDraft, direction: 1 | -1): QuickMoveDraft => ({
    ...draft,
    endMinutes: stepOnGrid(draft.endMinutes, direction),
});

/**
 * 시트 값을 검증하고 전송용 ISO 를 만든다.
 * 원래와 같으면(ms 동일) 규칙과 무관하게 `unchanged: true` — 요청을 보내지 않는다.
 */
export const validateQuickMoveDraft = (
    original: QuickMoveTimeOriginal,
    draft: QuickMoveDraft
): QuickMoveTimeValidation => {
    if (!isValidDateKey(draft.date) || !Number.isFinite(draft.startMinutes) || !Number.isFinite(draft.endMinutes)) {
        return { ok: false, code: 'INVALID_DATE', message: '날짜와 시간을 다시 선택해주세요.' };
    }

    const startTime = toIsoAt(draft.date, draft.startMinutes);
    const endTime = toIsoAt(draft.date, draft.endMinutes);
    const originalDateKey = toSmartScheduleKst(original.startTime).format('YYYY-MM-DD');
    const durationMinutes = durationOf(draft);

    if (sameInstant(startTime, original.startTime) && sameInstant(endTime, original.endTime)) {
        return { ok: true, unchanged: true, startTime, endTime, dateChanged: false, durationMinutes };
    }

    if (draft.startMinutes < 0 || draft.startMinutes >= QUICK_MOVE_DAY_MINUTES) {
        return { ok: false, code: 'START_OUT_OF_DAY', message: '시작 시각은 선택한 날짜 안이어야 해요.' };
    }
    if (draft.endMinutes > QUICK_MOVE_DAY_MINUTES) {
        return { ok: false, code: 'CROSSES_MIDNIGHT', message: '자정을 넘길 수 없어요.' };
    }
    if (draft.endMinutes <= draft.startMinutes) {
        return { ok: false, code: 'END_NOT_AFTER_START', message: '종료 시각은 시작 시각보다 늦어야 해요.' };
    }
    const minDuration = getQuickMoveMinDurationMinutes(original);
    if (durationMinutes < minDuration) {
        return { ok: false, code: 'TOO_SHORT', message: `최소 ${minDuration}분 이상이어야 해요.` };
    }

    return {
        ok: true,
        unchanged: false,
        startTime,
        endTime,
        dateChanged: draft.date !== originalDateKey,
        durationMinutes,
    };
};

/** ±10분 버튼 활성 여부. 누른 결과가 규칙을 통과할 때만 true. */
export const getQuickMoveShiftAvailability = (
    original: QuickMoveTimeOriginal,
    draft: QuickMoveDraft
): QuickMoveShiftAvailability => {
    const passes = (candidate: QuickMoveDraft) => validateQuickMoveDraft(original, candidate).ok;
    return {
        startMinus: passes(shiftQuickMoveStart(draft, -1)),
        startPlus: passes(shiftQuickMoveStart(draft, 1)),
        endMinus: passes(shiftQuickMoveEnd(draft, -1)),
        endPlus: passes(shiftQuickMoveEnd(draft, 1)),
    };
};

/** 분 → 'HH:mm'. 1440(다음 날 00:00)은 '24:00' 으로 보여준다. */
export const formatQuickMoveClock = (minutes: number): string => {
    const total = Math.round(minutes);
    const hours = Math.floor(total / 60);
    const mins = ((total % 60) + 60) % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/** 로캘 설정과 무관하게 비교 카드의 KST 날짜·요일을 같은 형식으로 표시한다. */
export const formatQuickMoveDate = (value: string): string => {
    if (!value) return '날짜 선택';
    const date = toSmartScheduleKst(value);
    return `${date.format('M월 D일')} (${['일', '월', '화', '수', '목', '금', '토'][date.day()]})`;
};
